import { GridSize } from '@mui/material';
import type { ApplicationEntity, SupplementaryApplicationEntity, SourceOfWealth as SourceOfWealthType, SourceOfWealthFields } from '@shared/api';
import { TextUtils } from '@shared';

interface FieldDef {
  name: keyof SourceOfWealthFields;
  label?: string;
  mask?: string;
  width?: GridSize;
}

const supplementaryApplicationFieldsRequiredAll: (keyof SupplementaryApplicationEntity)[] = [
  'mainTitle',
  'mainFirstName',
  'mainLastName',
  //'lastFourNumberOfCard',
  'relationshipToMain',
  'title',
  'firstName',
  'lastName',
  'nameOnCard',
  'maritalStatus',
  'dob',
  'mobileNumber',
  'passport_IDCardNo',
  'passportExpiryDate',
  'residentialAddressLine1',
  'residentialAddressLine2',
  'city',
  'postcode',
  'country',
  'countryOfIssuance',
  'email',
  'securityQuestion',
  'securityAnswer',
];

const supplementaryApplicationFieldsRequiredForSave: (keyof SupplementaryApplicationEntity)[] = [
  'mainTitle',
  'mainFirstName',
  'mainLastName',
  //'lastFourNumberOfCard',
  'relationshipToMain',
  'title',
  'firstName',
  'lastName',
  'nameOnCard',
  'maritalStatus',
  'dob',
  'passport_IDCardNo',
  'countryOfIssuance',
  'passportExpiryDate',
];

const applicationRequiredFieldsForSave: (keyof ApplicationEntity)[] = [
  'partnerAccountManagerId',
  'ippGroup',
  'title',
  'firstName',
  'lastName',
  'nameOnCard',
  'maritalStatus',
  'dob',
  'passport_IDCardNo',
  'passportExpiryDate',
  'countryOfIssuance',
  'productName',
];

const applicationRequiredFieldsAll: (keyof ApplicationEntity)[] = [
  'partnerAccountManagerId',
  'ippGroup',
  'title',
  'firstName',
  'lastName',
  'nameOnCard',
  'maritalStatus',
  'dob',
  'passport_IDCardNo',
  'passportExpiryDate',
  'residentialAddressLine1',
  'residentialAddressLine2',
  'city',
  'postcode',
  'country',
  'countryOfBirth',
  'countryOfIssuance',
  'mobileAreaCode',
  'mobileNumber',
  'email',
  'deliveryAddress',
  'employmentStatus',
  'positionHeld',
  'companyName',
  'employmentDuration',
  'natureOfBusiness',
  'personalAssistantLanguage',
  'annualIncomeCurrency',
  'annualIncomeAmount',
  'sow',
  'sof',
  'bankAccountNumber',
  'anticipatedMonthlySpend',
  'securityQuestion',
  'securityAnswer',
];

export const FIELDS: Record<SourceOfWealthType, (FieldDef | keyof SourceOfWealthFields)[]> = {
  Employment: [
    { name: 'position', width: 3 },
    { name: 'employer', width: 3 },
    { name: 'country', width: 3 },
    { name: 'date', label: 'Date of employment', width: 3 },
    { name: 'economicActivity', width: 12 },
  ],
  FamilyWealth: [
    { name: 'description', width: 4 },
    { name: 'relation', width: 4 },
    { name: 'country', width: 4 },
  ],
  Inheritance: [
    { name: 'description', width: 3 },
    { name: 'relation', width: 3 },
    { name: 'country', width: 3 },
    { name: 'year', label: 'Year of inheritance', width: 3, mask: '9999' },
  ],
  Business: [
    { name: 'employer', label: 'Name', width: 6 },
    { name: 'country', label: 'Location', width: 6 },
    { name: 'economicActivity', width: 12 },
  ],
  SalesOfAssets: [
    { name: 'asset', width: 3 },
    { name: 'value', width: 3 },
    { name: 'country', label: 'Location', width: 3 },
    { name: 'date', label: 'Date of sale', width: 3 },
  ],
  Savings: ['sourceOfSavings'],
  Guarantor: [{ name: 'customer', width: 12 }],
};

export class ValidateForm {
  static getRequiredSupplementaryApplicationFields: (forSave?: boolean, forSubmit?: boolean) => (keyof SupplementaryApplicationEntity)[] = (
    forSave?: boolean,
    forSubmit?: boolean,
  ) =>
    forSubmit
      ? supplementaryApplicationFieldsRequiredAll
      : forSave
        ? supplementaryApplicationFieldsRequiredForSave
        : supplementaryApplicationFieldsRequiredAll;

  static validateSupplementaryApplication = (applicationData: SupplementaryApplicationEntity, forSave?: boolean, forSubmit?: boolean) => {
    const requiredFields = this.getRequiredSupplementaryApplicationFields(forSave, forSubmit);
    const emptyFields =
      requiredFields?.filter((f) => {
        if (f === 'acceptedTC') return !applicationData[f];
        return applicationData[f] === '' || applicationData[f] === null || typeof applicationData[f] === 'undefined';
      }) ?? [];
    let errors: string[] = [];
    if (emptyFields.length) {
      errors = [
        ...errors,
        ...emptyFields.map((f) => {
          if (f === 'acceptedTC') return 'You should accept Terms and Conditions';
          return `${TextUtils.pascalToWords(f)} is required`;
        }),
      ];
    }
    if (applicationData.dob) {
      const now = new Date();
      const maxDob = new Date(now.getFullYear() - 18, now.getMonth(), now.getDate());
      const dobDate = new Date(applicationData.dob);
      const dob = new Date(dobDate.getFullYear(), dobDate.getMonth(), now.getDate());
      if (dob > maxDob) {
        errors.push('The age should be over 18 years');
      }
    }
    if (applicationData.passportExpiryDate) {
      if (new Date(applicationData.passportExpiryDate) <= new Date(new Date().getTime() + 24 * 60 * 60 * 1000)) {
        errors.push('The passport/ID should be valid');
      }
    }
    if (applicationData.postcode && applicationData.postcode.length > 10) {
      errors.push('Post code should be at most 10 characters');
    }
    if (errors.length > 0) {
      throw new Error(errors.join('\n'));
    }
  };

  static getRequiredApplicationFields: (forSave?: boolean, forSubmit?: boolean) => (keyof ApplicationEntity)[] = (
    forSave?: boolean,
    forSubmit?: boolean,
  ) => (forSubmit ? applicationRequiredFieldsAll : forSave ? applicationRequiredFieldsForSave : applicationRequiredFieldsAll);

  static validateApplication = (applicationData: ApplicationEntity, forSave?: boolean, forSubmit?: boolean) => {
    const requiredFields = [...this.getRequiredApplicationFields(forSave, forSubmit)];
    // deliveryAddress magic - remove from required fields if deliveryBordierOffice is filled
    const i = requiredFields.indexOf('deliveryAddress');
    if (i >= 0) {
      if (applicationData.deliveryBordierOffice) requiredFields.splice(i, 1);
    }
    // deliveryAddress magic 2 - require additional mailing address if selected
    if (applicationData.deliveryAddress === 'Alternative Address') {
      requiredFields.push('alternativeMailingAddressLine1');
      requiredFields.push('alternativeMailingAddressLine2');
      requiredFields.push('alternativeMailingAddressCity');
      requiredFields.push('alternativeMailingAddressPostcode');
      requiredFields.push('alternativeMailingAddressCountry');
    }

    const emptyFields =
      requiredFields?.filter((f) => {
        if (f === 'acceptedTC') return !applicationData[f];
        return applicationData[f] === '' || applicationData[f] === null || typeof applicationData[f] === 'undefined';
      }) ?? [];
    let errors: string[] = [];
    if (emptyFields.length) {
      errors = [
        ...errors,
        ...emptyFields.map((f) => {
          if (f === 'acceptedTC') return 'You should accept Terms and Conditions';
          if (f === 'ippGroup') return 'Circle is required';
          if (f === 'partnerAccountManagerId') return 'Partner Account Manager is required';
          return `${TextUtils.pascalToWords(f)} is required`;
        }),
      ];
    }
    if (applicationData.dob) {
      const now = new Date();
      const maxDob = new Date(now.getFullYear() - 18, now.getMonth(), now.getDate());
      const dobDate = new Date(applicationData.dob);
      const dob = new Date(dobDate.getFullYear(), dobDate.getMonth(), now.getDate());
      if (dob > maxDob) errors.push('The age should be over 18 years');
    }
    if (applicationData.passportExpiryDate) {
      if (new Date(applicationData.passportExpiryDate) <= new Date(new Date().getTime() + 24 * 60 * 60 * 1000)) errors.push('Passport/ID is expired');
    }
    // bankAccountNumber
    if (!!applicationData.bankAccountNumber && !applicationData.bankAccountNumber.match(/[a-zA-Z0-9]{6}\.\d\d\.\d\d\.[a-zA-Z]{3}/gim))
      errors.push('Bank Account Number is invalid');

    // currency and limit
    if (!applicationData.eurCurrency && !applicationData.chfCurrency && !applicationData.usdCurrency) errors.push('Currency is required');

    if (!applicationData.eurCreditLimit && !applicationData.chfCreditLimit && !applicationData.usdCreditLimit)
      errors.push('Credit Limit is required');

    const bankAccountNumberCurrency = applicationData.bankAccountNumber?.toUpperCase().slice(-3);
    if (!applicationData.eurCurrency && bankAccountNumberCurrency === 'EUR') {
      errors.push('Bank Account currency does not match product currency');
    } else if (!applicationData.usdCurrency && bankAccountNumberCurrency === 'USD') {
      errors.push('Bank Account currency does not match product currency');
    } else if (!applicationData.chfCurrency && bankAccountNumberCurrency === 'CHF') {
      errors.push('Bank Account currency does not match product currency');
    }
    //
    if (forSubmit || !forSave) {
      applicationData.sof?.forEach((item) => {
        if (!item.source) errors.push('Source of Funding is required');
        else {
          const fields = FIELDS[item.source];
          if (fields) {
            fields.forEach((field) => {
              const fieldName = typeof field === 'string' ? field : field.name;
              if (!item[fieldName]) errors.push('Details of Source of Funding is required');
            });
          }
        }
      });
      applicationData.sow?.forEach((item) => {
        if (!item.source) errors.push('Source of Wealth is required');
        else {
          const fields = FIELDS[item.source];
          if (fields) {
            fields.forEach((field) => {
              const fieldName = typeof field === 'string' ? field : field.name;
              if (!item[fieldName]) errors.push('Details of Source of Wealth is required');
            });
          }
        }
      });
      if (applicationData.nationalities) {
        const length = applicationData.nationalities.filter((item) => !item).length;
        if (length > 0) errors.push('Some citizenship by investment are missing');
      }
      if (applicationData.pep) {
        if (!applicationData.pepRolePosition) errors.push('PEP position is required');
        if (!applicationData.currentPepDate) errors.push('PEP Date of appointment is required');
      }
      if (applicationData.formerPep) {
        if (!applicationData.formerPepPosition) errors.push('Former PEP position is required');
        if (!applicationData.formerPepDate) errors.push('Former PEP Date of appointment is required');
      }
      if (applicationData.familyPep) {
        if (!applicationData.familyPepRelationship) errors.push('Family PEP relationship is required');
        if (!applicationData.familyPepPosition) errors.push('Family PEP position is required');
        if (!applicationData.familyPepDate) errors.push('Family PEP Date of appointment is required');
      }
    }
    if (errors.length > 0) throw new Error(errors.join('\n'));
  };
}
